import React from 'react';
import HamburgerMenu from '../global/menu';
import { BRANDING } from '../config';
import AdminInsightsPanel from './AdminInsightsPanel';
import PromptTemplateEditor from './PromptTemplateEditor';
import InsightsScheduleCard from './InsightsScheduleCard';
import '../global/styles.css';

// Logo loaded dynamically from config
const ClientLogo = require(`../assets/images/${BRANDING.logoFilename}`);

function Admin({ userInfo, isAdmin = false, onLogout, onNavigate }) {
  
  // Only allow admins to access this screen
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have permission to access this area.</p>
          <button 
            onClick={() => onNavigate('home')}
            className="btn btn-primary btn-full"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Lucide React icons (commonly used, free, professional)
  const UsersIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="admin-icon">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="8.5" cy="7" r="4"/>
      <path d="M20 8v6M23 11h-6"/>
    </svg>
  );

  const ClipboardIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="admin-icon">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <path d="M12 11h4"/>
      <path d="M12 16h4"/>
      <path d="M8 11h.01"/>
      <path d="M8 16h.01"/>
    </svg>
  );

  const BarChartIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="admin-icon">
      <line x1="12" x2="12" y1="20" y2="10"/>
      <line x1="18" x2="18" y1="20" y2="4"/>
      <line x1="6" x2="6" y1="20" y2="16"/>
    </svg>
  );

  const DownloadIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7,10 12,15 17,10"/>
      <line x1="12" x2="12" y1="15" y2="3"/>
    </svg>
  );

  const FileTextIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="admin-icon">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="16" x2="8" y1="13" y2="13"/>
      <line x1="16" x2="8" y1="17" y2="17"/>
      <line x1="10" x2="8" y1="9" y2="9"/>
    </svg>
  );

  const DownloadIcon2 = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="admin-icon">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="12" x2="12" y1="18" y2="12"/>
      <polyline points="9,15 12,18 15,15"/>
    </svg>
  );

  return (
    <div className="container">
      <HamburgerMenu 
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div className="content">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <img
            src={ClientLogo}
            alt={BRANDING.companyFullName}
            className="logo-location"
            style={{ marginBottom: '20px' }}
          />
          <h1 className="title">Admin Dashboard</h1>
          <p className="subtitle">
            Welcome {userInfo?.firstName}! Manage system administration tasks and generate reports.
          </p>
        </div>

        {/* Admin Menu Grid - FIXED WITH CSS CLASS */}
        <div className="admin-grid">
          {/* Manage Users */}
          <div className="card admin-card" style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--primary-color)', marginBottom: '20px' }}>
              <UsersIcon />
            </div>
            <h3 className="admin-title" style={{ 
              color: 'var(--text-color)', 
              fontSize: '18px', 
              fontWeight: '600',
              marginBottom: '15px'
            }}>
              Manage Users
            </h3>
            <p style={{
              color: 'var(--text-muted)',
              fontSize: '14px',
              lineHeight: '1.4',
              marginBottom: '25px'
            }}>
              Add, edit, and manage user accounts and permissions
            </p>
            <button 
              className="btn btn-primary btn-full"
              onClick={() => onNavigate('users')}
            >
              ACCESS USER MANAGEMENT
            </button>
          </div>

          {/* Chain Of Custody Report */}
          <div className="card admin-card" style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--primary-color)', marginBottom: '20px' }}>
              <ClipboardIcon />
            </div>
            <h3 className="admin-title" style={{ 
              color: 'var(--text-color)', 
              fontSize: '18px', 
              fontWeight: '600',
              marginBottom: '15px'
            }}>
              Chain Of Custody Report
            </h3>
            <p style={{
              color: 'var(--text-muted)',
              fontSize: '14px',
              lineHeight: '1.4',
              marginBottom: '25px'
            }}>
              Generate and view chain of custody reports for controlled substances
            </p>
            <button 
              className="btn btn-primary btn-full"
              onClick={() => onNavigate('chain-of-custody-report')}
            >
              ACCESS CHAIN OF CUSTODY
            </button>
          </div>

          {/* Provider Usage Report */}
          <div className="card admin-card" style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--primary-color)', marginBottom: '20px' }}>
              <BarChartIcon />
            </div>
            <h3 className="admin-title" style={{
              color: 'var(--text-color)',
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '15px'
            }}>
              Provider Usage Report
            </h3>
            <p style={{
              color: 'var(--text-muted)',
              fontSize: '14px',
              lineHeight: '1.4',
              marginBottom: '25px'
            }}>
              View provider usage statistics and medication consumption data
            </p>
            <button
              className="btn btn-primary btn-full"
              onClick={() => onNavigate('provider-usage-report')}
            >
              ACCESS USAGE REPORT
            </button>
          </div>

          {/* Inventory Report */}
          <div className="card admin-card" style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--primary-color)', marginBottom: '20px' }}>
              <DownloadIcon2 />
            </div>
            <h3 className="admin-title" style={{
              color: 'var(--text-color)',
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '15px'
            }}>
              Inventory Report
            </h3>
            <p style={{
              color: 'var(--text-muted)',
              fontSize: '14px',
              lineHeight: '1.4',
              marginBottom: '25px'
            }}>
              Generate inventory reports across multiple locations for any time period
            </p>
            <button
              className="btn btn-primary btn-full"
              onClick={() => onNavigate('inventory-report')}
            >
              ACCESS INVENTORY REPORT
            </button>
          </div>

          {/* Accountability Submissions */}
          <div className="card admin-card" style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--primary-color)', marginBottom: '20px' }}>
              <FileTextIcon />
            </div>
            <h3 className="admin-title" style={{
              color: 'var(--text-color)',
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '15px'
            }}>
              Accountability Submissions
            </h3>
            <p style={{
              color: 'var(--text-muted)',
              fontSize: '14px',
              lineHeight: '1.4',
              marginBottom: '25px'
            }}>
              View and export complete usage accountability form submissions
            </p>
            <button
              className="btn btn-primary btn-full"
              onClick={() => onNavigate('accountability-submissions')}
            >
              VIEW SUBMISSIONS
            </button>
          </div>
        </div>

        {/* AI Compliance Insights Section */}
        <div style={{ marginTop: '40px' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: 'var(--text-color)',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            AI Compliance Insights
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <AdminInsightsPanel />
            <InsightsScheduleCard />
            <PromptTemplateEditor />
          </div>
        </div>

      </div>

      <footer className="footer">
        <p className="text-small">
          Admin Dashboard - NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC
        </p>
      </footer>
    </div>
  );
}

export default Admin;
